
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, concatMap, Observable, tap } from 'rxjs';
import { AuthFactory, AuthState, ProfileLocation, TMHCorporateAdminAuthState } from '../../components/auth';
import { ApiResponse } from '../../definitions/api-response.interface';
import { ApiService } from '../api/api.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { ApplicationPath, ErrorMessage } from './shared-enums';
import { LoggedInUser, UnreadNotification, UnreadNotificationPayload, UserDetails } from './shared-interface';
import { SharedUtilService } from './shared-util.service';
import { SharedService } from './shared.service';

@Injectable({
	providedIn: 'root'
})
export class SharedApiService 
{
	constructor(private readonly apiService: ApiService,
		private readonly sharedService: SharedService,
		private readonly sharedUtilService : SharedUtilService,
		private readonly router: Router,
		private readonly localStorageService: LocalStorageService){}
	private readonly flagValue$: BehaviorSubject<{ tnc: boolean;policyFlag: boolean }> =
		new BehaviorSubject<{ tnc: boolean;policyFlag: boolean }>({ tnc: false, policyFlag: false });
	get flagObservable$(): Observable<{ tnc: boolean;policyFlag: boolean }>
	{
		return this.flagValue$;
	}
	get flagValue(): { tnc: boolean;policyFlag: boolean }
	{
		return this.flagValue$.value;
	}

	private readonly isUnreadNotification$: BehaviorSubject<boolean> =
		new BehaviorSubject<boolean>(false);
	get isUnreadNotificationObservable$(): Observable<boolean>
	{
		return this.isUnreadNotification$;
	}
	get isUnreadNotification(): boolean 
	{
		return this.isUnreadNotification$.value;
	}
	set updatedUnreadNotificationState(value: boolean)
	{
		this.isUnreadNotification$.next(value);
	}
	user: AuthState = AuthFactory.buildState();
	
	updateLoggedInUserDetails() 
	{
		this.apiService.getLoggedInUserObs().subscribe({
			next: (res: ApiResponse) => 
			{
				const user: AuthState = <AuthState>(res.data);
				if (user) 
				{
					this.mapUserSession(user);
				}
			},
			error: () =>
			{
				this.sharedUtilService.triggerErrorService(ErrorMessage.commonError, 100, '401');
				this.sharedService.navigateMethod(this.router, ApplicationPath.authError);
			}
		});
		this.apiService.getLoggedInUserLocations().subscribe({
			next: (res: ApiResponse) => 
			{
				const locations:ProfileLocation = <ProfileLocation>(res.data);
				if (locations)
				{
					this.localStorageService.setLoggedInUserLocation(this.localStorageService.striginifyJSON(locations));
				}
			},
			error: () =>
			{
				this.sharedUtilService.triggerErrorService(ErrorMessage.commonError, 100, '401');
				this.sharedService.navigateMethod(this.router, ApplicationPath.authError);
			}
		});
	}
	
	getUnreadNotificationsCount() 
	{
		const payload: UnreadNotificationPayload = {
			contactGRID: '',
			locations: [],
			portalUserID: '',
			roles: []
		};
		this.sharedService
			.getLoggedInUseLocationsIdsObs()
			.pipe(
				tap((locationIds: string[]) => 
				{
					payload.locations = locationIds;
				}),
				concatMap(() => this.sharedService.getLoggedInUserObs()),
				tap((loggedInUser: LoggedInUser) => 
				{
					if (loggedInUser)
					{
						payload.roles = loggedInUser.roles ?? [];
						payload.portalUserID = this.sharedService.emptyStringHandler(loggedInUser.userId);
						payload.contactGRID = this.sharedService.emptyStringHandler( loggedInUser.contactGRID);
						if (!payload.contactGRID)
							payload.contactID = this.sharedService.emptyStringHandler(loggedInUser.contactID);
					}
				}),
				concatMap(() => this.apiService.getUnreadNotificationCount(payload))
			  )
			.subscribe({
				next: (res: ApiResponse) => 
				{
					const unreadNotifcation: UnreadNotification = <UnreadNotification>(res.data);
					this.updatedUnreadNotificationState = unreadNotifcation.count > 0;
				}
			});
	}

	getLoggedInUserDetails()  : Observable<ApiResponse>
	{
		return this.apiService.getLoggedInUserObs();
	}

	getLoggedInUserLocations() : Observable<ApiResponse>
	{
		return this.apiService.getLoggedInUserLocations();
	}
	getTmhCorporateAdminDetails()  : Observable<ApiResponse>
	{
		return this.apiService.getLoggedInTmhCorporateAdminDetails();
	}
	getTmhCustomerSupportDetails()  : Observable<ApiResponse>
	{
		return this.apiService.getLoggedInTmhCustomerSupportDetails();
	}

	mapUserSession(user: AuthState)
	{
		this.user = user;
		this.localStorageService.setLoggedInUser(this.localStorageService.striginifyJSON(user));
		const userProfileFlag:{ tnc: boolean ; policyFlag: boolean }  = { tnc: false, policyFlag: false };

		userProfileFlag.tnc = user.tncUpdated;
		userProfileFlag.policyFlag = user.privacyPolicyUpdated;
		this.flagValue$.next(userProfileFlag);

		this.sharedService.getuserNameObs().subscribe((userName) => 
		{
			this.sharedService.updatedUserNameState = userName;
		});
	}
	mapTmhCorporateAdminUserSession(user: TMHCorporateAdminAuthState)
	{
		this.localStorageService.setTmhCorporateAdminDetails(this.localStorageService.striginifyJSON(user));
		this.sharedService.getuserNameObs().subscribe((userName) => 
		{
			this.sharedService.updatedUserNameState = userName;
		});
	}
	mapTmhCustomerSupportUserSession(user: TMHCorporateAdminAuthState)
	{
		this.localStorageService.setTmhCustomerSupportDetails(this.localStorageService.striginifyJSON(user));
		this.sharedService.getuserNameObs().subscribe((userName) => 
		{
			this.sharedService.updatedUserNameState = userName;
		});
	}
	mapUserLocations(locations: ProfileLocation)
	{
		this.localStorageService.setLoggedInUserLocation(this.localStorageService.striginifyJSON(locations));
	}
	
	updateUserSession(userDetails: UserDetails)
	{
		const userProfile = this.localStorageService.getLoggedInUser();
		if (userProfile && !this.sharedService.isEmptyObject(userProfile))
		{
			const user:LoggedInUser = <LoggedInUser> this.localStorageService.parseJSON(userProfile);
			user.firstName = userDetails.firstName;
			user.lastName = userDetails.lastName;
			user.jobTitle = userDetails.jobTitle;
			user.phoneNumber = userDetails.phoneNumber;
			
			this.localStorageService.setLoggedInUser(this.localStorageService.striginifyJSON(user));
					
			this.sharedService.getuserNameObs().subscribe((userName) => 
			{
				this.sharedService.updatedUserNameState = userName;
			});
		}
	}
}
